import * as React from "react"
import {
  StyledPanelBodySection,
  StyledPanelBoilerplate,
} from "gatsby-interface"
import { CmsAuthorizationForm } from "@modules/cms/authorization/components/CmsAuthorizationForm"
import { AuthorizedForm } from "@modules/cms/authorization/components/AuthorizedForm"
import { AuthorizingWindow } from "@modules/cms/authorization/components/AuthorizingWindow"
import { OnboardingSiteType } from "@modules/onboarding/shared/types"
import { VerificationStatus } from "@modules/cms/shared/utils/types"
import { useCmsAuthStatus } from "../hooks/useCmsAuthStatus"
import { cmsIntegration as cmsIntegrationText } from "@modules/locales/default.js"
import { interpolateMessage } from "@modules/locales"
import { CmsVendor } from "@modules/graphql/types"

type CmsAuthorizationModalProps = {
  isOpen: boolean
  siteId: string
  closeModal: () => void
  orgSiteName: string
  isConnected: boolean
  name: CmsVendor
  formattedName: string
  siteType: OnboardingSiteType
  refetch?: () => void
}

export function CmsAuthorizationModal({
  isOpen,
  siteId,
  closeModal,
  orgSiteName,
  isConnected,
  name: cmsVendor,
  formattedName,
  siteType = OnboardingSiteType.Create,
  refetch,
}: CmsAuthorizationModalProps) {
  const [
    modalStatus,
    handleAuthorized,
    handleAuthorizing,
    requestAuthorization,
  ] = useCmsAuthStatus(
    cmsVendor,
    siteId,
    isConnected
      ? VerificationStatus.Authorized
      : VerificationStatus.Authorization
  )

  return (
    <StyledPanelBoilerplate
      isOpen={isOpen}
      aria-label={interpolateMessage<"vendorName">(
        cmsIntegrationText.headers.connectIntegrationLong,
        { vendorName: formattedName }
      )}
      header={interpolateMessage<"vendorName">(
        cmsIntegrationText.headers.connectIntegration,
        { vendorName: formattedName }
      )}
      onClose={closeModal}
    >
      {modalStatus === VerificationStatus.Authorization && (
        <StyledPanelBodySection>
          <CmsAuthorizationForm
            siteId={siteId}
            siteName={orgSiteName}
            siteType={siteType}
            cmsVendor={cmsVendor}
            cmsVendorName={formattedName}
            onCancel={closeModal}
            onAuthorize={handleAuthorizing}
            onComplete={handleAuthorized}
          />
        </StyledPanelBodySection>
      )}

      {modalStatus === VerificationStatus.Authorizing && (
        <StyledPanelBodySection>
          <AuthorizingWindow
            siteType={siteType}
            siteId={siteId}
            cmsVendor={cmsVendor}
            formattedName={formattedName}
            onAuthorizing={handleAuthorizing}
          />
        </StyledPanelBodySection>
      )}

      {modalStatus === VerificationStatus.Authorized && (
        <AuthorizedForm
          siteType={siteType}
          siteId={siteId}
          cmsVendor={cmsVendor}
          orgSiteName={orgSiteName}
          refetch={refetch}
          onFinish={closeModal}
          onCancel={closeModal}
          onFetchError={requestAuthorization}
        />
      )}
    </StyledPanelBoilerplate>
  )
}
